import { Check, ChevronDown } from 'lucide-react'
import { Fragment, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type KeyboardEvent, type ReactNode } from 'react'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { controlClass, pickerClass } from './controlClass'
import { Popover } from './Popover'

/** `label` is the text (typeahead, accessibility); `node`, when set, is what the list and the field show (e.g. a colored status). */
export type SelectOption = {
  value: string
  label: string
  node?: ReactNode
  disabled?: boolean
  /** A bold header opens each run of options with the same group. */
  group?: string
}

type SelectProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'onChange' | 'children'> & {
  value: string
  onValueChange: (value: string) => void
  options: SelectOption[]
  /** Adds an empty-value option at the top ("All", "None"…). */
  placeholder?: string
  /** A value must be chosen: the placeholder shows while empty but is not offered as an option. */
  required?: boolean
  size?: 'sm' | 'md'
  /** What the closed field shows for the chosen option; the list always shows the bare options. */
  display?: (option: SelectOption) => ReactNode
}

/**
 * Custom listbox so opening, highlighting and closing can animate like every
 * other floating surface. Keyboard: ↑ ↓ Home End to move, Enter/Space to pick,
 * Esc to close, letters to jump; a letter typed on the closed field opens it
 * and starts the search with that letter.
 */
export function Select({ value, onValueChange, options, placeholder, required, size = 'md', display, className, disabled, id, ...aria }: SelectProps) {
  const t = useT()
  const listId = useId()
  const all: SelectOption[] = placeholder !== undefined && !required ? [{ value: '', label: placeholder }, ...options] : options
  const selected = all.find((option) => option.value === value)
  const showPlaceholder = !selected || selected.value === ''
  // The letter that opened the list from the closed field.
  const [typed, setTyped] = useState('')

  return (
    <div className={cn('min-w-0', className)}>
      <Popover
        kind="listbox"
        matchWidth
        className="min-w-[180px] p-1"
        trigger={({ open, toggle, ...popupAria }) => (
          <button
            id={id}
            type="button"
            role="combobox"
            aria-controls={open ? listId : undefined}
            disabled={disabled}
            onClick={() => {
              setTyped('')
              toggle()
            }}
            onKeyDown={(event) => {
              if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
                event.preventDefault()
                setTyped('')
                toggle(true)
              } else if (isTyping(event)) {
                event.preventDefault()
                setTyped(event.key)
                toggle(true)
              }
            }}
            {...popupAria}
            {...aria}
            className={cn(
              pickerClass(!showPlaceholder),
              'flex items-center gap-2 pr-2.5 text-left',
              size === 'sm' ? 'h-[calc(var(--spacing)*8*var(--ui-size))] text-[length:calc(13px*var(--text-scale)*var(--ui-size))]' : 'h-[calc(var(--spacing)*9*var(--ui-size))]',
              open && 'border-accent shadow-[0_0_0_3px_var(--accent-soft)]',
            )}
          >
            <span className={cn('min-w-0 flex-1 truncate', showPlaceholder && 'font-normal text-subtle')}>
              {selected ? (display ? display(selected) : (selected.node ?? selected.label)) : (placeholder ?? t('common.select'))}
            </span>
            <ChevronDown
              className={cn(
                'size-3.5 shrink-0 text-subtle transition-transform duration-[var(--dur-base)] ease-[var(--ease-spring)]',
                open && 'rotate-180 text-muted',
              )}
            />
          </button>
        )}
      >
        {(close) => (
          <Listbox
            id={listId}
            options={all}
            value={value}
            typed={typed}
            onPick={(next) => {
              close()
              if (next !== value) onValueChange(next)
            }}
            onClose={close}
          />
        )}
      </Popover>
    </div>
  )
}

/** A printable key without a modifier: what typing into a closed picker starts with. */
export const isTyping = (event: KeyboardEvent) => event.key.length === 1 && /\S/.test(event.key) && !event.ctrlKey && !event.metaKey && !event.altKey

/** Lists longer than this get a search box (matching ignores case and extra spaces). */
const SEARCH_FROM = 8
const normalize = (text: string) => text.trim().replace(/\s+/g, ' ').toLowerCase()

function Listbox({
  id,
  options: all,
  value,
  typed: opening,
  onPick,
  onClose,
}: {
  id: string
  options: SelectOption[]
  value: string
  typed: string
  onPick: (value: string) => void
  onClose: () => void
}) {
  const t = useT()
  const list = useRef<HTMLUListElement>(null)
  const searchBox = useRef<HTMLInputElement>(null)
  const typed = useRef({ text: '', at: 0 })
  const searchable = all.length > SEARCH_FROM
  const [query, setQuery] = useState(searchable ? opening : '')
  const needle = normalize(query)
  // The empty choice ("All", "None") always stays reachable while searching.
  const options = needle ? all.filter((option) => option.value === '' || normalize(option.label).includes(needle)) : all
  const [active, setActive] = useState(() => {
    if (opening && searchable) return Math.max(0, options.findIndex((option) => option.value !== '' && !option.disabled))
    const jump = opening ? all.findIndex((option) => !option.disabled && option.label.toLowerCase().startsWith(opening.toLowerCase())) : -1
    return Math.max(0, jump >= 0 ? jump : all.findIndex((option) => option.value === value))
  })

  useEffect(() => {
    if (searchable) {
      const box = searchBox.current
      box?.focus({ preventScroll: true })
      box?.setSelectionRange(box.value.length, box.value.length)
    } else list.current?.focus({ preventScroll: true })
  }, [searchable])

  useEffect(() => {
    list.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const move = (from: number, step: number) => {
    for (let i = 1; i <= options.length; i++) {
      const next = (from + step * i + options.length) % options.length
      if (!options[next].disabled) return next
    }
    return from
  }

  const onKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setActive((index) => move(index, 1))
        return
      case 'ArrowUp':
        event.preventDefault()
        setActive((index) => move(index, -1))
        return
      case 'Home':
        if (searchable) return
        event.preventDefault()
        setActive(move(-1, 1))
        return
      case 'End':
        if (searchable) return
        event.preventDefault()
        setActive(move(options.length, -1))
        return
      case 'Enter':
        event.preventDefault()
        if (options[active] && !options[active].disabled) onPick(options[active].value)
        return
      case ' ':
        if (searchable) return
        event.preventDefault()
        if (options[active] && !options[active].disabled) onPick(options[active].value)
        return
      case 'Tab':
        onClose()
        return
      default:
        if (searchable) return
        if (event.key.length === 1 && /\S/.test(event.key)) {
          const now = Date.now()
          typed.current = { text: (now - typed.current.at < 600 ? typed.current.text : '') + event.key.toLowerCase(), at: now }
          const found = options.findIndex((option) => !option.disabled && option.label.toLowerCase().startsWith(typed.current.text))
          if (found >= 0) setActive(found)
        }
    }
  }

  return (
    <div className="flex flex-col gap-1">
      {searchable && (
        <input
          ref={searchBox}
          type="search"
          value={query}
          placeholder={t('common.search')}
          aria-label={t('common.search')}
          aria-controls={id}
          aria-activedescendant={options.length ? `${id}-${active}` : undefined}
          onChange={(event) => {
            setQuery(event.target.value)
            setActive(0)
          }}
          onKeyDown={onKeyDown}
          className={cn(controlClass, 'h-8 text-[length:calc(13px*var(--text-scale))] [&::-webkit-search-cancel-button]:hidden')}
        />
      )}
      <ul
        ref={list}
        id={id}
        role="listbox"
        tabIndex={-1}
        aria-activedescendant={`${id}-${active}`}
        onKeyDown={onKeyDown}
        className="max-h-72 overflow-y-auto outline-none"
      >
        {options.map((option, index) => {
          const isSelected = option.value === value
          const isActive = index === active
          return (
            <Fragment key={option.value || '__empty'}>
              {option.group && option.group !== options[index - 1]?.group && (
                <li role="presentation" className="px-2.5 pt-2 pb-1 text-[length:calc(12.5px*var(--text-scale))] font-semibold text-fg">
                  {option.group}
                </li>
              )}
              <li
                id={`${id}-${index}`}
                data-index={index}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled || undefined}
                onPointerMove={() => !option.disabled && setActive(index)}
                onClick={() => !option.disabled && onPick(option.value)}
                className={cn(
                  'relative flex h-8.5 cursor-pointer select-none items-center gap-2 rounded-[calc(9px*var(--radius-scale))] px-2.5 text-[length:calc(13.5px*var(--text-scale))] transition-colors duration-100',
                  isActive ? 'bg-hover text-fg' : 'text-fg-2',
                  option.value === '' && 'text-muted',
                  isSelected && option.value !== '' && 'font-medium text-fg',
                  option.disabled && 'cursor-not-allowed opacity-40',
                )}
              >
                <span className="min-w-0 flex-1 truncate">{option.node ?? option.label}</span>
                {isSelected && <Check className="animate-scale-in size-3.5 shrink-0 text-accent" strokeWidth={2.5} />}
              </li>
            </Fragment>
          )
        })}
        {needle && !options.some((option) => option.value !== '') && (
          <li className="px-2.5 py-2 text-[length:calc(13px*var(--text-scale))] text-muted">{t('common.noMatches')}</li>
        )}
      </ul>
    </div>
  )
}
